import { useMemo, useRef } from 'react';
import Chart from 'react-apexcharts';
import { useTranslation } from 'react-i18next';
import { ApexOptions } from 'apexcharts';
import { Card, CardBody, CardHeader } from 'grommet';
import { ColorType } from 'grommet/utils';
import { Box, BoxProps, LoadingSpinner, Text } from '/src/components';
import { DashboardData } from '/src/lib/models';
import theme from '/src/theme';

export const RevenueByProgramChart: React.FC<RevenueByProgramChartProps> = (props) => {
  /* Props */
  const { data, hideHeader, height, ...cardProps } = props;
  const { t } = useTranslation();

  /* Memos */
  const seriesData = useMemo(() => {
    const sortedCategories = data.percent_revenue_per_program.sort((a, b) =>
      a.program.toLowerCase() > b.program.toLowerCase() ? 1 : -1
    );

    const seriesData: Record<string, number> = {};

    sortedCategories.forEach(({ program, percentage }) => {
      seriesData[program] = parseFloat(percentage.toString());
    });

    return seriesData;
  }, [data]);

  const chartData = useMemo(() => Object.values(seriesData), [seriesData]);
  const chartLabels = useMemo(() => Object.keys(seriesData), [seriesData]);

  /* Refs */
  const themeColors = useRef(theme.global?.colors ?? {});
  const cardHeight = useRef('32.5rem');
  const chartColors = useRef([
    themeColors.current['accent-3'],
    themeColors.current['accent-5'],
    themeColors.current['brand'],
    themeColors.current['accent-1'],
    themeColors.current['accent-4'],
    themeColors.current['accent-7'],
    themeColors.current['accent-6'],
    themeColors.current['accent-2'],
  ] as ColorType[]);
  const chartOptions = useRef({
    chart: {
      type: 'donut',
      fontFamily: 'Roboto Condensed, sans-serif',
      foreColor: themeColors.current['dark-3'] as string,
      animations: {
        enabled: true,
        speed: 500,
        animateGradually: {
          enabled: false,
        },
        dynamicAnimation: {
          speed: 300,
        },
      },
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['#fff'],
    },
    plotOptions: {
      pie: {
        donut: {
          size: '75%',
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '14px',
              fontWeight: 500,
            },
            value: {
              show: true,
              fontSize: '24px',
              fontWeight: 600,
              formatter: (val) => `${val}%`,
            },
          },
        },
      },
    },
    tooltip: {
      enabled: false,
    },
    colors: chartColors.current,
    legend: {
      fontSize: '16px',
      position: 'bottom',
      markers: {
        radius: 12,
      },
      itemMargin: {
        horizontal: 8,
        vertical: 4,
      },
    },
  } as ApexOptions);

  /* Render */
  return (
    <Card height={height || cardHeight.current} {...cardProps}>
      {!hideHeader && (
        <CardHeader>
          <Text size="xlarge">{t('dashboard:percentRevenueByProgram')}</Text>
        </CardHeader>
      )}
      <CardBody pad="none" gap="none">
        {!chartData || !chartLabels ? (
          <LoadingSpinner />
        ) : (
          <Box alignSelf="center" pad="1.5rem" fill>
            <Chart
              options={{ ...chartOptions.current, labels: chartLabels }}
              series={chartData}
              type="donut"
              width="100%"
              height="100%"
            />
          </Box>
        )}
      </CardBody>
    </Card>
  );
};

export type RevenueByProgramChartProps = BoxProps & {
  data: DashboardData;
  hideHeader?: boolean;
};
